var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useImmer } from 'use-immer';
import { EventBus } from '../../utils/EventBus';
import { getLevelScenarios } from '../../api/scenarios';
import { getLevelById } from '../../api/levels';
import './Review.scss';
import { DialogueEditor } from '../DialogueEditor/DialogueEditor';
const Review = ({ levelId, visible = true }) => {
    const [state, updateState] = useImmer({ groups: {} });
    useEffect(() => {
        // --- Subscribe to events ---
        const unsubscribers = [
            EventBus.on('dialogueUpdated', dialogue => handleDialogueChange(dialogue, 'updated')),
            EventBus.on('dialogueDeleted', id => handleDialogueRemoval(id, 'dialogue')),
            EventBus.on('eventUpdated', ev => handleEventChange(ev, 'updated')),
            EventBus.on('eventDeleted', id => handleEventRemoval(id)),
            EventBus.on('scenarioUpdated', sc => markScenarioApplied(sc.id)),
            EventBus.on('scenarioUpdateFailed', payload => markScenarioFailed(payload.scenarioId, payload.error)),
            EventBus.on('scenarioUpdateAttempt', p => markScenarioAttempt(p.scenarioId)),
            EventBus.on('mapLinkUpdated', p => markScenariosAppliedViaMap(p.scenarioIds)),
            EventBus.on('systemAttempt', p => handleSystemAttempt(p)),
            EventBus.on('systemSuccess', p => handleSystemSuccess(p)),
            EventBus.on('systemError', p => handleSystemError(p)),
        ];
        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, []);
    const handleDialogueChange = (dialogue, action) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!dialogue)
            return;
        const lvlId = (_a = dialogue.levelId) !== null && _a !== void 0 ? _a : levelId;
        const level = yield getLevelById(lvlId);
        let scenarios = yield getLevelScenarios(lvlId);
        // If the API returned an empty array, try to derive scenarios from level.nodes
        if (scenarios.length === 0) {
            const scenarioIds = new Set();
            // level.nodes may be undefined or a JSON string
            const nodeObj = typeof level.nodes === 'string' ? JSON.parse(level.nodes) : (level.nodes || {});
            Object.values(nodeObj).forEach(n => {
                var _a;
                (_a = n.interactiveScenarioIds) === null || _a === void 0 ? void 0 : _a.forEach(id => scenarioIds.add(id));
            });
            const promises = Array.from(scenarioIds).map(id => getScenarioByIdSafe(lvlId, id));
            const fetched = (yield Promise.all(promises)).filter(Boolean);
            scenarios = fetched;
        }
        scenarios.forEach(sc => {
            const uses = scenarioUsesDialogue(sc, dialogue.id);
            if (uses) {
                const coords = findScenarioCoords(level.nodes, sc.id);
                const change = {
                    id: generateId(),
                    date: new Date(),
                    type: 'dialogue',
                    action,
                    objectId: dialogue.id,
                    objectName: dialogue.name,
                    systems: [{ name: 'Scenario', applied: false }],
                    logs: [
                        `[${new Date().toLocaleTimeString()}] Dialogue change detected (action:${action})`,
                        `Linked scenarios processed later`
                    ]
                };
                addChange(sc, coords, change);
            }
        });
    });
    const handleEventChange = (ev, action) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!ev)
            return;
        const lvlId = (_a = ev.levelId) !== null && _a !== void 0 ? _a : levelId;
        const level = yield getLevelById(lvlId);
        let scenarios = yield getLevelScenarios(lvlId);
        if (scenarios.length === 0) {
            const nodeObj = typeof level.nodes === 'string' ? JSON.parse(level.nodes) : (level.nodes || {});
            const scenarioIds = new Set();
            Object.values(nodeObj).forEach(n => { var _a; return (_a = n.interactiveScenarioIds) === null || _a === void 0 ? void 0 : _a.forEach(id => scenarioIds.add(id)); });
            const fetched = (yield Promise.all(Array.from(scenarioIds).map(id => getScenarioByIdSafe(lvlId, id)))).filter(Boolean);
            scenarios = fetched;
        }
        scenarios.forEach(sc => {
            const uses = scenarioUsesEvent(sc, ev.id);
            if (uses) {
                const coords = findScenarioCoords(level.nodes, sc.id);
                const change = {
                    id: generateId(),
                    date: new Date(),
                    type: 'event',
                    action,
                    objectId: ev.id,
                    objectName: ev.title,
                    systems: [{ name: 'Scenario', applied: false }],
                    logs: [
                        `[${new Date().toLocaleTimeString()}] Event change detected (action:${action})`
                    ]
                };
                addChange(sc, coords, change);
            }
        });
    });
    const handleDialogueRemoval = (_dialogueId, _type) => __awaiter(void 0, void 0, void 0, function* () {
        // Not enough data to know name; we still push entry with id.
        // Ideally we would fetch by id but could be deleted. We'll label name = id.
    });
    const handleEventRemoval = (_eventId) => __awaiter(void 0, void 0, void 0, function* () {
        // TODO: optionally implement logic similar to handleDialogueRemoval
    });
    const addChange = (scenario, coords, change) => {
        updateState(draft => {
            const grp = draft.groups[scenario.id];
            if (!grp) {
                draft.groups[scenario.id] = {
                    scenario,
                    mapCoords: coords,
                    changes: [change],
                };
            }
            else {
                // Merge
                grp.mapCoords = Array.from(new Set([...grp.mapCoords, ...coords]));
                grp.changes.push(change);
            }
        });
    };
    const removeChange = (scenarioId, changeId) => {
        updateState(draft => {
            const grp = draft.groups[scenarioId];
            if (!grp)
                return;
            grp.changes = grp.changes.filter(c => c.id !== changeId);
            if (grp.changes.length === 0) {
                delete draft.groups[scenarioId];
            }
        });
    };
    const markScenarioApplied = (scenarioId) => {
        updateState(draft => {
            const grp = draft.groups[scenarioId];
            if (!grp)
                return;
            grp.changes.forEach(ch => {
                const sys = ch.systems.find(s => s.name === 'Scenario');
                if (sys && !sys.applied) {
                    sys.applied = true;
                    sys.appliedAt = new Date();
                    sys.error = undefined;
                    ch.logs.push(`[${new Date().toLocaleTimeString()}] Scenario saved successfully`);
                }
            });
        });
    };
    const markScenarioFailed = (scenarioId, error) => {
        updateState(draft => {
            const grp = draft.groups[scenarioId];
            if (!grp)
                return;
            grp.changes.forEach(ch => {
                const sys = ch.systems.find(s => s.name === 'Scenario');
                if (sys && !sys.applied) {
                    sys.error = error || 'unknown error';
                    ch.logs.push(`[${new Date().toLocaleTimeString()}] Scenario save FAILED: ${sys.error}`);
                }
            });
        });
    };
    const markScenarioAttempt = (scenarioId) => {
        updateState(draft => {
            const grp = draft.groups[scenarioId];
            if (!grp) {
                // cache (used only for the Saving scenario baseline)
                return; // nothing
            }
            grp.changes.forEach(ch => {
                const sys = ch.systems.find(s => s.name === 'Scenario');
                if (sys && !sys.error) {
                    ch.logs.push(`[${new Date().toLocaleTimeString()}] Saving scenario...`);
                }
            });
        });
    };
    const markScenariosAppliedViaMap = (ids) => {
        updateState(draft => {
            ids.forEach(id => {
                const grp = draft.groups[id];
                if (!grp)
                    return;
                grp.changes.forEach(ch => {
                    const sys = ch.systems.find(s => s.name === 'Scenario');
                    if (sys && !sys.applied) {
                        sys.applied = true;
                        sys.appliedAt = new Date();
                        ch.logs.push(`[${new Date().toLocaleTimeString()}] Map link updated (auto-applied)`);
                    }
                });
            });
        });
    };
    const getScenarioNodes = (sc) => {
        if (!sc.nodes)
            return [];
        if (Array.isArray(sc.nodes))
            return sc.nodes;
        try {
            return JSON.parse(sc.nodes);
        }
        catch (_a) {
            return [];
        }
    };
    const scenarioUsesDialogue = (sc, dialogueId) => {
        const nodes = getScenarioNodes(sc);
        return nodes.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.dialogueId) === dialogueId; });
    };
    const scenarioUsesEvent = (sc, eventId) => {
        const nodes = getScenarioNodes(sc);
        return nodes.some((n) => { var _a; return ((_a = n === null || n === void 0 ? void 0 : n.data) === null || _a === void 0 ? void 0 : _a.eventId) === eventId; });
    };
    const findScenarioCoords = (nodes, scenarioId) => {
        if (!nodes)
            return [];
        const nodeObj = typeof nodes === 'string' ? JSON.parse(nodes) : nodes;
        return Object.entries(nodeObj)
            .filter(([_, n]) => { var _a; return (_a = n.interactiveScenarioIds) === null || _a === void 0 ? void 0 : _a.includes(scenarioId); })
            .map(([coord]) => coord);
    };
    const generateId = () => Math.random().toString(36).substring(2, 9);
    const getScenarioByIdSafe = (lvlId, id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const { getScenarioById } = yield import('../../api/scenarios');
            return yield getScenarioById(lvlId, id);
        }
        catch (_a) {
            return null;
        }
    });
    // --- Render ---
    const scenarioEntries = Object.values(state.groups);
    // Compute the statistics
    const totalChanges = scenarioEntries.reduce((acc, group) => acc + group.changes.length, 0);
    const appliedChanges = scenarioEntries.reduce((acc, group) => acc + group.changes.filter(ch => ch.systems.every(sys => sys.applied)).length, 0);
    const failedChanges = scenarioEntries.reduce((acc, group) => acc + group.changes.filter(ch => ch.systems.some(sys => sys.error)).length, 0);
    const pendingChanges = totalChanges - appliedChanges - failedChanges;
    const content = scenarioEntries.length === 0 ? (_jsxs("div", { className: "review-empty", children: [_jsx("div", { className: "empty-icon", children: "\u25A1" }), _jsx("div", { className: "empty-title", children: "No Changes to Review" }), _jsx("div", { className: "empty-description", children: "Changes to dialogues and events will appear here for tracking their application in scenarios" })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "review-header", children: [_jsx("h2", { children: "Review Changes" }), _jsxs("div", { className: "review-stats", children: [_jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-icon", children: "\u25A0" }), _jsx("span", { className: "stat-value", children: totalChanges }), _jsx("span", { className: "stat-label", children: "total" })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-icon", children: "\u2713" }), _jsx("span", { className: "stat-value", children: appliedChanges }), _jsx("span", { className: "stat-label", children: "applied" })] }), _jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-icon", children: "\u25CB" }), _jsx("span", { className: "stat-value", children: pendingChanges }), _jsx("span", { className: "stat-label", children: "pending" })] }), failedChanges > 0 && (_jsxs("div", { className: "stat-item", children: [_jsx("span", { className: "stat-icon", children: "\u2715" }), _jsx("span", { className: "stat-value", children: failedChanges }), _jsx("span", { className: "stat-label", children: "failed" })] }))] })] }), _jsx("div", { className: "review-container", children: scenarioEntries.map(group => (_jsxs("div", { className: "scenario-review-group", children: [_jsxs("div", { className: "scenario-header", children: [_jsx("div", { className: "scenario-info", children: _jsxs("h3", { children: ["Scenario ", group.scenario.name] }) }), group.mapCoords.length > 0 && (_jsxs("div", { className: "map-coords", children: [_jsx("span", { className: "coords-label", children: "Map Nodes:" }), group.mapCoords.map(coord => (_jsx("span", { className: "coord-tag", children: coord }, coord)))] }))] }), _jsxs("div", { className: "changes-table", children: [_jsxs("div", { className: "table-header", children: [_jsx("div", { className: "col-time", children: "Time" }), _jsx("div", { className: "col-event", children: "Event" }), _jsx("div", { className: "col-object", children: "Object" }), _jsx("div", { className: "col-who", children: "Who" }), _jsx("div", { className: "col-actions" })] }), [...group.changes].sort((a, b) => a.date.getTime() - b.date.getTime()).map(ch => (_jsxs("div", { className: "change-row", children: [_jsxs("div", { className: "row-primary", children: [_jsx("div", { className: "col-time", children: ch.date.toLocaleTimeString() }), _jsxs("div", { className: "col-event", children: [ch.type === 'dialogue' ? 'Dialogue' : 'Event', " ", ch.action] }), _jsx("div", { className: "col-object", children: ch.objectName }), _jsx("div", { className: "col-who", children: DialogueEditor.name }), _jsxs("div", { className: "col-actions", children: [_jsx("button", { className: "action-btn debug-btn", onClick: (e) => {
                                                                const row = e.currentTarget.closest('.change-row');
                                                                const panel = row === null || row === void 0 ? void 0 : row.querySelector('.debug-panel');
                                                                if (panel)
                                                                    panel.classList.toggle('visible');
                                                            }, children: "Debug" }), _jsx("button", { className: "action-btn approve-btn", onClick: () => removeChange(group.scenario.id, ch.id), children: "Approve" })] })] }), ch.systems.filter(sys => sys.name === 'Scenario').map(sys => {
                                            var _a;
                                            return (_jsxs("div", { className: "row-secondary", children: [_jsx("div", { className: "col-time", children: sys.applied ? (_a = sys.appliedAt) === null || _a === void 0 ? void 0 : _a.toLocaleTimeString() :
                                                            sys.error ? 'error' : 'pending' }), _jsx("div", { className: "col-event", children: sys.applied ? 'Applied' : sys.error ? 'Failed' : 'Processing' }), _jsx("div", { className: "col-object", children: "scenario update" }), _jsx("div", { className: "col-who", children: sys.name }), _jsx("div", { className: "col-actions" })] }, sys.name));
                                        }), _jsxs("div", { className: "debug-panel", children: [_jsx("div", { className: "debug-header", children: "Debug Information" }), _jsx("div", { className: "debug-content", children: ch.logs.map((l, idx) => (_jsx("div", { className: "debug-line", children: l }, idx))) })] })] }, ch.id)))] })] }, group.scenario.id))) })] }));
    const ensureSystemEntry = (grp, system) => {
        grp.changes.forEach(ch => {
            let sys = ch.systems.find(s => s.name === system);
            if (!sys) {
                sys = { name: system, applied: false };
                ch.systems.push(sys);
            }
        });
    };
    const handleSystemAttempt = (p) => {
        updateState(draft => {
            const grp = draft.groups[p.scenarioId];
            if (!grp)
                return;
            ensureSystemEntry(grp, p.system);
            grp.changes.forEach(ch => {
                const sys = ch.systems.find(s => s.name === p.system);
                if (sys && !sys.error) {
                    ch.logs.push(`[${new Date().toLocaleTimeString()}] [${p.system}] starting task`);
                }
            });
        });
    };
    const handleSystemSuccess = (p) => {
        updateState(draft => {
            const grp = draft.groups[p.scenarioId];
            if (!grp)
                return;
            ensureSystemEntry(grp, p.system);
            grp.changes.forEach(ch => {
                const sys = ch.systems.find(s => s.name === p.system);
                if (sys && !sys.applied) {
                    sys.applied = true;
                    sys.appliedAt = new Date();
                    sys.error = undefined;
                    ch.logs.push(`[${new Date().toLocaleTimeString()}] [${p.system}] completed`);
                }
            });
        });
    };
    const handleSystemError = (p) => {
        updateState(draft => {
            const grp = draft.groups[p.scenarioId];
            if (!grp)
                return;
            ensureSystemEntry(grp, p.system);
            grp.changes.forEach(ch => {
                const sys = ch.systems.find(s => s.name === p.system);
                if (sys && !sys.applied) {
                    sys.error = p.error || 'unknown error';
                    ch.logs.push(`[${new Date().toLocaleTimeString()}] [${p.system}] FAILED: ${sys.error}`);
                }
            });
        });
    };
    return (_jsx("div", { className: "review-main", style: { display: visible ? 'block' : 'none' }, children: content }));
};
export default Review;
